import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/i18n';
import type { PolicyApprovalPending } from '@/lib/domain';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { type Check, apiError, idempotencyKey, moneyStep } from './lib';

type Mode = 'amount' | 'percent';

function isApprovalPending(body: unknown): body is PolicyApprovalPending {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { status?: unknown }).status === 'APPROVAL_PENDING'
  );
}

/**
 * `POST /checks/:id/discount` — the one check-level endpoint the dashboard has
 * never called. It exists, the POS calls it, and it is gated on its own key
 * (`discounts.apply`), separate from `checks.update`.
 *
 * It has **three** outcomes and all three are drawn here, because a one-click
 * "Apply discount" is a UI that cannot represent its own endpoint:
 *
 *  - **ALLOW** — the updated check comes back, 200.
 *  - **REQUIRE_APPROVAL** — **202**, and the discount has NOT been applied. A
 *    voided charge is parked so the bill still records what was asked, and a
 *    `PolicyApproval` row goes to whoever can sign it. Reporting this as success
 *    would tell a cashier the guest owes less than they do.
 *  - **DENY** — 403 in the policy's own words.
 *
 * Two more things the endpoint insists on, so the form does too. `reason` is
 * `@IsNotEmpty` — it is written onto the charge and into the approval request,
 * and it is the only record of why. And amount and percent are **mutually
 * exclusive**: sending both is a 400, so this is one control with a mode rather
 * than two fields anyone could fill in at once.
 *
 * The threshold is metered against the bill's RUNNING credit including parked
 * pending discounts, not against `check.subtotal`, so the second 10% on a bill
 * can be refused where the first was allowed. Nothing here tries to predict
 * that — the server owns the decision and says which one it made.
 */
export default function DiscountDialog({
  check,
  onClose,
  onDone,
}: {
  check: Check;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<Mode>('percent');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  /** A 202 is not an error and not a success — it gets its own resting state. */
  const [parked, setParked] = useState<PolicyApprovalPending | null>(null);

  const numeric = Number(value);
  const invalid =
    !value.trim() ||
    !Number.isFinite(numeric) ||
    numeric <= 0 ||
    (mode === 'percent' && numeric > 100);

  const submit = async () => {
    setSaving(true);
    try {
      const { data, status } = await api.post(`/checks/${check.id}/discount`, {
        ...(mode === 'amount' ? { amount: numeric } : { percent: numeric }),
        reason: reason.trim(),
        // §34 — a replay must not stack a second discount charge, nor raise a
        // second approval request for the one already parked.
        operationId: idempotencyKey('discount'),
      });
      if (status === 202 || isApprovalPending(data)) {
        setParked(data as PolicyApprovalPending);
        onDone();
        return;
      }
      toast.success(t('billing.discount.applied'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('billing.discount.failed')));
    } finally {
      setSaving(false);
    }
  };

  if (parked) {
    return (
      <Dialog
        open
        onClose={onClose}
        title={t('billing.discount.parkedTitle')}
        footer={<Button onClick={onClose}>{t('common.close')}</Button>}
      >
        <div className="space-y-3 text-sm">
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-amber-700 dark:text-amber-400">
            {parked.message}
          </p>
          <p className="text-ink-muted">
            {t('billing.discount.parkedHint')}
          </p>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('billing.discount.title')}
      description={t('billing.discount.subtitle')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={saving || invalid || !reason.trim()}>
            {t('billing.discount.apply')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <fieldset>
          <legend className="mb-2 text-sm font-medium">
            {t('billing.discount.mode')}
          </legend>
          <div className="flex flex-wrap gap-4 text-sm">
            {(['percent', 'amount'] as const).map((option) => (
              <label key={option} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="discount-mode"
                  className="h-4 w-4"
                  checked={mode === option}
                  onChange={() => {
                    setMode(option);
                    setValue('');
                  }}
                />
                {option === 'percent'
                  ? t('billing.discount.percent')
                  : t('billing.discount.amount')}
              </label>
            ))}
          </div>
        </fieldset>

        <Field
          label={
            mode === 'percent'
              ? t('billing.discount.percentLabel')
              : t('billing.discount.amountLabel')
          }
          required
          hint={
            mode === 'percent'
              ? t('billing.discount.percentHint')
              : t('billing.discount.amountHint', {
                  currency: check.currency,
                })
          }
        >
          <Input
            type="number"
            dir="ltr"
            min="0"
            max={mode === 'percent' ? '100' : undefined}
            step={mode === 'percent' ? '0.01' : moneyStep(check.currency)}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />
        </Field>

        {/* `@IsNotEmpty()` on the DTO, and the only record of why the venue gave
            money away — it is written onto the charge and into the approval. */}
        <Field label={t('billing.discount.reason')} required>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>

        <p className="text-xs text-ink-muted">
          {t('billing.discount.against', {
            subtotal: formatCurrency(check.subtotal, check.currency, i18n.language),
          })}
        </p>
      </div>
    </Dialog>
  );
}
